package domain.player

import engine.model.BufferMode
import engine.model.DasPreservation
import engine.model.HandlingConfig
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

/**
 * Surrogate for [HandlingConfig] (from the Ktris library, which cannot be annotated).
 * All enum fields are stored as their [Enum.name] strings so we avoid annotating
 * [DasPreservation] and [BufferMode] directly.
 */
@Serializable
private data class HandlingConfigSurrogate(
    val dasDelay: Double = 167.0,
    val arrDelay: Double = 33.0,
    val softDropFactor: Double = 35.0,
    val dcdDelay: Double = 0.0,
    val dasCutOnRotation: Boolean = false,
    val dasPreservation: String = DasPreservation.FULL.name,
    val cancelDasOnDirectionChange: Boolean = true,
    val preventAccidentalHardDrop: Boolean = true,
    val irsMode: String = BufferMode.HOLD.name,
    val ihsMode: String = BufferMode.HOLD.name,
)

/**
 * Custom [KSerializer] for [HandlingConfig] that delegates to [HandlingConfigSurrogate].
 */
object HandlingConfigSerializer : KSerializer<HandlingConfig> {

    private val delegate = HandlingConfigSurrogate.serializer()

    override val descriptor: SerialDescriptor = delegate.descriptor

    override fun serialize(encoder: Encoder, value: HandlingConfig) {
        val surrogate = HandlingConfigSurrogate(
            dasDelay = value.dasDelay,
            arrDelay = value.arrDelay,
            softDropFactor = value.softDropFactor,
            dcdDelay = value.dcdDelay,
            dasCutOnRotation = value.dasCutOnRotation,
            dasPreservation = value.dasPreservation.name,
            cancelDasOnDirectionChange = value.cancelDasOnDirectionChange,
            preventAccidentalHardDrop = value.preventAccidentalHardDrop,
            irsMode = value.irsMode.name,
            ihsMode = value.ihsMode.name,
        )
        delegate.serialize(encoder, surrogate)
    }

    override fun deserialize(decoder: Decoder): HandlingConfig {
        val surrogate = delegate.deserialize(decoder)
        return HandlingConfig(
            dasDelay = surrogate.dasDelay,
            arrDelay = surrogate.arrDelay,
            softDropFactor = surrogate.softDropFactor,
            dcdDelay = surrogate.dcdDelay,
            dasCutOnRotation = surrogate.dasCutOnRotation,
            dasPreservation = enumValueOf<DasPreservation>(surrogate.dasPreservation),
            cancelDasOnDirectionChange = surrogate.cancelDasOnDirectionChange,
            preventAccidentalHardDrop = surrogate.preventAccidentalHardDrop,
            irsMode = enumValueOf<BufferMode>(surrogate.irsMode),
            ihsMode = enumValueOf<BufferMode>(surrogate.ihsMode),
        )
    }
}
